import { apiClient } from './api.service';

// ──────────────────────────────────────────────────
// Types (mirrored from web app's tenantSettingTypes)
// ──────────────────────────────────────────────────

export const CLIO_REPLICATION_SCHEDULER_ENTITIES = [
  'clio-users',
  'clio-contacts',
  'clio-matters',
  'clio-activities',
  'clio-wip',
  'clio-activity-descriptions',
  'clio-expense-categories',
  'clio-utbms-codes',
  'clio-utbms-sets',
  'clio-bank-accounts',
  'clio-bank-transactions',
  'clio-bills',
  'clio-line-items',
  'clio-allocations',
  'clio-groups',
  'clio-credit-memos',
  'clio-trust-line-items',
  'clio-activity-rates',
  'clio-custom-field-sets',
  'clio-custom-fields',
  'clio-currencies',
  'clio-matter-stages',
  'juniper-eligible-matter-view',
] as const;

export type ClioReplicationSchedulerEntity = typeof CLIO_REPLICATION_SCHEDULER_ENTITIES[number];
export type ReplicateToDWConditionType = 'startDate' | 'rollingMonthsWindow';

export const CLIO_REPLICATION_REPORT_KINDS = [
  'accounts_receivable',
  'accounts_receivable_aging',
  'fee_allocation',
  'matter',
  'matter_balance_summary',
  'revenue',
  'trust_management',
] as const;

export type ClioReplicationReportKind = typeof CLIO_REPLICATION_REPORT_KINDS[number];

export interface ClioReplicationSchedulerEntityConfig {
  replicateToClio: boolean;
  replicateToDW: boolean;
  replicateToDWConditionType?: ReplicateToDWConditionType;
  replicateToDWValue?: string | number;
}

export type ClioReplicationSchedulerEntityMap = Record<ClioReplicationSchedulerEntity, ClioReplicationSchedulerEntityConfig>;

export interface ClioReplicationReportReplicationConfig {
  enabled: boolean;
  periodModel: 'CALENDAR' | 'FISCAL';
  fiscalPattern: '4-4-5' | '4-5-4' | '5-4-4';
  weekStartDay: 'MONDAY' | 'SUNDAY';
  fiscalYearAnchorDate: string;
  week53Handling: 'P12' | 'P11' | 'nearest-end';
  finalizeClosedPeriods: boolean;
  backfillDefaultPeriods: 12 | 24 | 36;
  reports: Record<ClioReplicationReportKind, boolean>;
  reportExportByMonth: Record<ClioReplicationReportKind, boolean>;
}

export interface ClioReplicationSchedulerConfig {
  enabled: boolean;
  entities: ClioReplicationSchedulerEntityMap;
  reportReplication: ClioReplicationReportReplicationConfig;
  settingsComponent?: string;
  healthCheckEndpoint?: string;
}

// ──────────────────────────────────────────────────
// Entity Options (labels and descriptions)
// ──────────────────────────────────────────────────

export interface EntityOption {
  key: ClioReplicationSchedulerEntity;
  label: string;
  description: string;
}

export const CLIO_REPLICATION_SCHEDULER_ENTITY_OPTIONS: EntityOption[] = [
  { key: 'clio-matters', label: 'Clio Matters', description: 'Batch + on-demand matter replication.' },
  { key: 'clio-activities', label: 'Clio Activities', description: 'Batch + on-demand activity replication.' },
  { key: 'clio-wip', label: 'Clio WIP', description: 'WIP cache + activity reconciliation processing.' },
  { key: 'clio-bills', label: 'Clio Bills', description: 'Batch + on-demand bill replication.' },
  { key: 'clio-contacts', label: 'Clio Contacts', description: 'Batch + on-demand contact replication.' },
  { key: 'clio-users', label: 'Clio Users', description: 'User replication.' },
  { key: 'clio-bank-accounts', label: 'Clio Bank Accounts', description: 'Bank account replication.' },
  { key: 'clio-bank-transactions', label: 'Clio Bank Transactions', description: 'Bank transaction replication.' },
  { key: 'clio-line-items', label: 'Clio Line Items', description: 'Bill line item replication.' },
  { key: 'clio-allocations', label: 'Clio Allocations', description: 'Allocation replication.' },
  { key: 'clio-activity-descriptions', label: 'Clio Activity Descriptions', description: 'Activity description replication.' },
  { key: 'clio-expense-categories', label: 'Clio Expense Categories', description: 'Expense category replication.' },
  { key: 'clio-utbms-codes', label: 'Clio UTBMS Codes', description: 'UTBMS code replication.' },
  { key: 'clio-utbms-sets', label: 'Clio UTBMS Sets', description: 'UTBMS set replication.' },
  { key: 'clio-groups', label: 'Clio Groups', description: 'Group replication.' },
  { key: 'clio-credit-memos', label: 'Clio Credit Memos', description: 'Credit memo replication.' },
  { key: 'clio-trust-line-items', label: 'Clio Trust Line Items', description: 'Trust line item replication.' },
  { key: 'clio-activity-rates', label: 'Clio Activity Rates', description: 'Activity rate replication.' },
  { key: 'clio-custom-field-sets', label: 'Clio Custom Field Sets', description: 'Custom field set replication.' },
  { key: 'clio-custom-fields', label: 'Clio Custom Fields', description: 'Custom field replication.' },
  { key: 'clio-currencies', label: 'Clio Currencies', description: 'Currency replication.' },
  { key: 'clio-matter-stages', label: 'Clio Matter Stages', description: 'Matter stage replication.' },
  { key: 'juniper-eligible-matter-view', label: 'Juniper Eligible Matter View', description: 'Pre-aggregated Juniper eligible matter view rebuild.' },
];

// ──────────────────────────────────────────────────
// Default Config
// ──────────────────────────────────────────────────

const DEFAULT_ENTITY_CONFIG: ClioReplicationSchedulerEntityConfig = {
  replicateToClio: true,
  replicateToDW: false,
};

const DEFAULT_DW_CONDITION_TYPE: ReplicateToDWConditionType = 'rollingMonthsWindow';
const DEFAULT_DW_ROLLING_MONTHS = 12;

const DEFAULT_REPORT_REPLICATION_CONFIG: ClioReplicationReportReplicationConfig = {
  enabled: true,
  periodModel: 'CALENDAR',
  fiscalPattern: '4-4-5',
  weekStartDay: 'MONDAY',
  fiscalYearAnchorDate: '2024-01-01',
  week53Handling: 'P12',
  finalizeClosedPeriods: true,
  backfillDefaultPeriods: 12,
  reports: Object.fromEntries(
    CLIO_REPLICATION_REPORT_KINDS.map(k => [k, true])
  ) as ClioReplicationReportReplicationConfig['reports'],
  reportExportByMonth: Object.fromEntries(
    CLIO_REPLICATION_REPORT_KINDS.map(k => [k, true])
  ) as ClioReplicationReportReplicationConfig['reportExportByMonth'],
};

const DEFAULT_ENTITY_MAP = CLIO_REPLICATION_SCHEDULER_ENTITIES.reduce(
  (acc, entity) => {
    acc[entity] = { ...DEFAULT_ENTITY_CONFIG };
    return acc;
  },
  {} as ClioReplicationSchedulerEntityMap
);

export const DEFAULT_CLIO_REPLICATION_SCHEDULER_CONFIG: ClioReplicationSchedulerConfig = {
  enabled: true,
  entities: DEFAULT_ENTITY_MAP,
  reportReplication: DEFAULT_REPORT_REPLICATION_CONFIG,
};

// ──────────────────────────────────────────────────
// Normalizer
// ──────────────────────────────────────────────────

export function normalizeClioReplicationSchedulerConfig(
  rawConfig?: Partial<ClioReplicationSchedulerConfig> | null
): ClioReplicationSchedulerConfig {
  const entities = { ...DEFAULT_ENTITY_MAP };
  const incomingEntities = rawConfig?.entities || {};

  CLIO_REPLICATION_SCHEDULER_ENTITIES.forEach(entity => {
    const rawValue = incomingEntities[entity];
    if (typeof rawValue === 'boolean') {
      entities[entity] = { ...DEFAULT_ENTITY_CONFIG, replicateToClio: rawValue };
      return;
    }

    if (rawValue && typeof rawValue === 'object') {
      const value = rawValue as Partial<ClioReplicationSchedulerEntityConfig>;
      entities[entity] = {
        ...DEFAULT_ENTITY_CONFIG,
        ...value,
        replicateToClio: value.replicateToClio !== false,
        replicateToDW: value.replicateToDW === true,
        replicateToDWConditionType: value.replicateToDWConditionType === 'startDate' ? 'startDate' : DEFAULT_DW_CONDITION_TYPE,
        replicateToDWValue: value.replicateToDWValue ?? DEFAULT_DW_ROLLING_MONTHS,
      };
      return;
    }

    entities[entity] = { ...DEFAULT_ENTITY_CONFIG };
  });

  const reportReplication = normalizeReportReplicationConfig(rawConfig?.reportReplication);

  return {
    ...DEFAULT_CLIO_REPLICATION_SCHEDULER_CONFIG,
    ...rawConfig,
    enabled: rawConfig?.enabled !== false,
    entities,
    reportReplication,
  };
}

function normalizeReportReplicationConfig(
  rawConfig?: Partial<ClioReplicationReportReplicationConfig>
): ClioReplicationReportReplicationConfig {
  const reports = { ...DEFAULT_REPORT_REPLICATION_CONFIG.reports };
  for (const [key, val] of Object.entries(rawConfig?.reports || {})) {
    reports[key as keyof typeof reports] = val !== false;
  }

  const reportExportByMonth = { ...DEFAULT_REPORT_REPLICATION_CONFIG.reportExportByMonth };
  for (const [key, val] of Object.entries(rawConfig?.reportExportByMonth || {})) {
    reportExportByMonth[key as keyof typeof reportExportByMonth] = val !== false;
  }

  return {
    ...DEFAULT_REPORT_REPLICATION_CONFIG,
    ...rawConfig,
    enabled: rawConfig?.enabled !== false,
    periodModel: rawConfig?.periodModel === 'FISCAL' ? 'FISCAL' : 'CALENDAR',
    fiscalPattern: (['4-4-5', '4-5-4', '5-4-4'] as const).includes(rawConfig?.fiscalPattern as any)
      ? rawConfig!.fiscalPattern as '4-4-5' | '4-5-4' | '5-4-4'
      : '4-4-5',
    weekStartDay: rawConfig?.weekStartDay === 'SUNDAY' ? 'SUNDAY' : 'MONDAY',
    fiscalYearAnchorDate: typeof rawConfig?.fiscalYearAnchorDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(rawConfig.fiscalYearAnchorDate)
      ? rawConfig.fiscalYearAnchorDate
      : DEFAULT_REPORT_REPLICATION_CONFIG.fiscalYearAnchorDate,
    week53Handling: (['P12', 'P11', 'nearest-end'] as const).includes(rawConfig?.week53Handling as any)
      ? rawConfig!.week53Handling as 'P12' | 'P11' | 'nearest-end'
      : 'P12',
    finalizeClosedPeriods: rawConfig?.finalizeClosedPeriods !== false,
    backfillDefaultPeriods: ([12, 24, 36] as const).includes(rawConfig?.backfillDefaultPeriods as any)
      ? rawConfig!.backfillDefaultPeriods as 12 | 24 | 36
      : 12,
    reports,
    reportExportByMonth,
  };
}

// ──────────────────────────────────────────────────
// API Service
// ──────────────────────────────────────────────────

class ReplicationConfigService {
  async getTenantConfig(tenantId: string) {
    const response = await apiClient.get(`/admin/tenants/${tenantId}/config`);
    return response.data;
  }

  async updateTenantReplicationConfig(tenantId: string, config: ClioReplicationSchedulerConfig) {
    const response = await apiClient.patch(`/admin/tenants/${tenantId}/config`, {
      modules: {
        'clio-replication-scheduler': config,
      },
    });
    return response.data;
  }

  async dropEntityTables(tenantId: string, entityKey: string) {
    const response = await apiClient.delete(`/admin/tenants/${tenantId}/dw/entities/${entityKey}/tables`);
    return response.data;
  }

  async dropReportTable(tenantId: string, reportKind: string) {
    const response = await apiClient.delete(`/admin/tenants/${tenantId}/dw/reports/${reportKind}/table`);
    return response.data;
  }

  async resyncReportMonth(tenantId: string, reportKind: string, monthCode: string) {
    const response = await apiClient.post(`/admin/tenants/${tenantId}/dw/reports/${reportKind}/resync-month`, { monthCode });
    return response.data;
  }

  async resyncSnapshotReport(tenantId: string, reportKind: string) {
    const response = await apiClient.post(`/admin/tenants/${tenantId}/dw/reports/${reportKind}/resync-snapshot`);
    return response.data;
  }

  async invokeDwSync(tenantId: string, type: 'all' | 'entities' | 'reports' = 'all') {
    const response = await apiClient.post(`/admin/tenants/${tenantId}/dw/invoke-sync`, { type });
    return response.data;
  }
}

export const replicationConfigService = new ReplicationConfigService();
